/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import { Table, Tooltip } from 'antd'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { ROUTES } from '@sportninja/common/constants/app'
import { Suspension } from '@sportninja/common/hooks/useUsersMeSuspensions'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'

/**
 * Props interface for the PlayerSuspensionsList component
 * @interface PlayerSuspensionsListProps
 */
interface PlayerSuspensionsListProps {
  /**
   * Array of suspension data to display
   */
  suspensions: Suspension[]
  /**
   * Loading state indicator
   */
  loading: boolean
  /**
   * Error object if request failed
   */
  error: Error | null
  /**
   * Function to refresh suspension data
   */
  refetch?: () => void
  /**
   * Flag to determine if the component is being viewed in a profile context
   */
  isProfileView?: boolean
  /**
   * Flag to determine if only active suspensions should be displayed
   */
  onlyDisplayActive?: boolean
  /**
   * Optional schedule ID to filter suspensions
   */
  scheduleId?: string | null
}

/**
 * Component that displays a list of player suspensions in a table
 * @param {PlayerSuspensionsListProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const PlayerSuspensionsList: React.FC<PlayerSuspensionsListProps> = ({
  suspensions,
  loading,
  error,
  isProfileView = true,
  onlyDisplayActive = false,
  scheduleId = null,
}) => {
  const { width } = useWindowSize()
  const isMobile = width <= 768

  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1)

  // Filter suspensions based on provided props and remove duplicates by ID
  const filteredSuspensions = suspensions
    .filter((s: Suspension) => {
      // Filter by active status if requested
      if (onlyDisplayActive) {
        return s.is_active
      }

      // Filter by schedule if provided
      if (scheduleId && s.schedule?.uid !== scheduleId) {
        return false
      }

      return true
    })
    // Remove duplicates by using a Map with suspension ID as key
    .reduce((unique: Suspension[], suspension: Suspension) => {
      // Skip suspensions without IDs
      if (!suspension.id) return [...unique, suspension]

      // Check if this ID is already in our unique array
      const exists = unique.some((item) => item.id === suspension.id)
      if (!exists) {
        return [...unique, suspension]
      }
      return unique
    }, [])

  // Columns configuration for the table
  const columns = [
    // Only show player column if not in profile view
    ...(!isProfileView
      ? [
          {
            title: 'Player',
            key: 'player',
            dataIndex: 'player',
            filterSearch: true,
            filters: filteredSuspensions
              .map(
                (s) =>
                  s.player?.full_name ||
                  `${s.player?.name_first || ''} ${s.player?.name_last || ''}`
              )
              .filter((name): name is string => !!name)
              .filter((value, index, self) => self.indexOf(value) === index)
              .map((name) => ({ text: name, value: name })),
            onFilter: (value: string, record: Suspension) =>
              (
                record.player?.full_name ||
                `${record.player?.name_first || ''} ${
                  record.player?.name_last || ''
                }`
              ).includes(value),
            sorter: (a: Suspension, b: Suspension) => {
              const nameA =
                a.player?.full_name ||
                `${a.player?.name_first || ''} ${a.player?.name_last || ''}`
              const nameB =
                b.player?.full_name ||
                `${b.player?.name_first || ''} ${b.player?.name_last || ''}`
              return nameA.localeCompare(nameB)
            },
            render: (_, record: Suspension) => (
              <Link
                to={`${ROUTES.PLAYER_ROOT}/${record.player?.id}`}
                css={css`
                  text-decoration: none;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <img
                    src={
                      getImageThumbnailId(record.player) ||
                      '/images/generic_placeholders/placeholder_player.png'
                    }
                    alt={record.player?.full_name || 'Player'}
                    css={css`
                      width: 32px;
                      height: 32px;
                      border-radius: 8px;
                      object-fit: contain;
                      border: 1px solid ${colors.NEUTRAL_0};
                    `}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      const target = e.target as HTMLImageElement
                      target.src =
                        '/images/generic_placeholders/placeholder_player.png'
                    }}
                  />
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                    `}
                  >
                    <Text
                      variant='paragraphSmall'
                      weight='medium'
                      color={colors.NEUTRAL_0}
                    >
                      {record.player?.full_name ||
                        `${record.player?.name_first || ''} ${
                          record.player?.name_last || ''
                        }`}
                    </Text>
                  </div>
                </div>
              </Link>
            ),
          },
        ]
      : []),
    {
      title: 'Team',
      key: 'team',
      dataIndex: 'team',
      filterSearch: true,
      filters: filteredSuspensions
        .map((s) => s.team?.name)
        .filter((name): name is string => !!name)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((name) => ({ text: name, value: name })),
      onFilter: (value: string, record: Suspension) =>
        record.team?.name?.includes(value) || false,
      sorter: (a: Suspension, b: Suspension) => {
        const teamA = a.team?.name || ''
        const teamB = b.team?.name || ''
        return teamA.localeCompare(teamB)
      },
      render: (_, record: Suspension) => (
        <Link
          to={`${ROUTES.TEAM_ROOT}/${record.team?.id}`}
          css={css`
            text-decoration: none;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <img
              src={
                getImageThumbnailId(record.team) ||
                '/images/generic_placeholders/placeholder_team.png'
              }
              alt={record.team?.name || 'Team'}
              css={css`
                width: 32px;
                height: 32px;
                border-radius: 8px;
                object-fit: contain;
                border: 1px solid ${colors.NEUTRAL_0};
              `}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const target = e.target as HTMLImageElement
                target.src = '/images/generic_placeholders/placeholder_team.png'
              }}
            />
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.NEUTRAL_0}
              >
                {record.team?.name || '-'}
              </Text>
              {isProfileView ? (
                <Link
                  to={`${ROUTES.SCHEDULE_ROOT}/${record.schedule?.uid}`}
                  css={css`
                    text-decoration: none;
                  `}
                >
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    {record.schedule?.name || '-'}
                  </Text>
                </Link>
              ) : null}
            </div>
          </div>
        </Link>
      ),
    },
    // Only show status if not filtering for active suspensions
    ...(!onlyDisplayActive
      ? [
          {
            title: 'Status',
            key: 'status',
            dataIndex: 'is_active',
            filters: [
              { text: 'Active', value: true },
              { text: 'Completed', value: false },
            ],
            onFilter: (value: boolean, record: Suspension) =>
              record.is_active === value,
            sorter: (a: Suspension, b: Suspension) => {
              if (a.is_active === b.is_active) return 0
              return a.is_active ? -1 : 1 // Active comes first
            },
            align: 'left',
            render: (_, record: Suspension) => (
              <div
                css={css`
                  display: flex;
                  justify-content: flex-start;
                  align-items: center;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={
                    record.is_active ? colors.ERROR_200 : colors.SUCCESS_200
                  }
                >
                  {record.is_active ? 'Active' : 'Completed'}
                </Text>
              </div>
            ),
          },
        ]
      : []),
    {
      title: 'Start Date',
      key: 'date',
      dataIndex: 'start_date',
      sorter: (a: Suspension, b: Suspension) => {
        if (!a.start_date) return -1
        if (!b.start_date) return 1
        return dayjs(a.start_date).unix() - dayjs(b.start_date).unix()
      },
      filters: filteredSuspensions
        .map((s) =>
          s.start_date ? dayjs(s.start_date).format('MMM D, YYYY') : null
        )
        .filter((date): date is string => !!date)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((date) => ({ text: date, value: date })),
      onFilter: (value: string, record: Suspension) =>
        record.start_date
          ? dayjs(record.start_date).format('MMM D, YYYY') === value
          : false,
      align: 'left',
      render: (_, record: Suspension) => (
        <div
          css={css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='medium'
            color={colors.NEUTRAL_0}
          >
            {record.start_date
              ? dayjs(record.start_date).format('MMM D, YYYY')
              : '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Return Date',
      key: 'returnDate',
      dataIndex: 'end_date',
      sorter: (a: Suspension, b: Suspension) => {
        if (!a.end_date) return -1
        if (!b.end_date) return 1
        return dayjs(a.end_date).unix() - dayjs(b.end_date).unix()
      },
      filters: filteredSuspensions
        .map((s) =>
          s.end_date ? dayjs(s.end_date).format('MMM D, YYYY') : null
        )
        .filter((date): date is string => !!date)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((date) => ({ text: date, value: date })),
      onFilter: (value: string, record: Suspension) =>
        record.end_date
          ? dayjs(record.end_date).format('MMM D, YYYY') === value
          : false,
      align: 'left',
      render: (_, record: Suspension) => (
        <div
          css={css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='medium'
            color={colors.NEUTRAL_0}
          >
            {record.end_date
              ? dayjs(record.end_date).format('MMM D, YYYY')
              : '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Penalty',
      key: 'penalty',
      dataIndex: 'enforcement',
      filterSearch: true,
      filters: filteredSuspensions
        .map((s) => s.enforcement?.name)
        .filter((name): name is string => !!name)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((name) => ({ text: name, value: name })),
      onFilter: (value: string, record: Suspension) =>
        record.enforcement?.name?.includes(value) || false,
      sorter: (a: Suspension, b: Suspension) => {
        const penaltyA = a.enforcement?.name || ''
        const penaltyB = b.enforcement?.name || ''
        return penaltyA.localeCompare(penaltyB)
      },
      align: 'left',
      render: (_, record: Suspension) => (
        <div
          css={css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
          `}
        >
          <Text
            variant='paragraphSmall'
            weight='medium'
            color={colors.NEUTRAL_0}
            style={`text-transform: capitalize;`}
          >
            {record.enforcement?.name || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Description',
      key: 'description',
      dataIndex: 'description',
      filterSearch: true,
      filters: filteredSuspensions
        .map((s) => s.description)
        .filter((desc): desc is string => !!desc)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((desc) => ({ text: desc, value: desc })),
      onFilter: (value: string, record: Suspension) =>
        record.description?.includes(value) || false,
      sorter: (a: Suspension, b: Suspension) => {
        const descA = a.description || ''
        const descB = b.description || ''
        return descA.localeCompare(descB)
      },
      align: 'left',
      render: (_, record: Suspension) => {
        const description = record.description || '-'
        const isTruncated = description.length > 15
        const displayText = isTruncated
          ? `${description.substring(0, 15)}...`
          : description

        return (
          <Tooltip
            title={description}
            placement='top'
            zIndex={10001}
            trigger='hover'
          >
            <div
              css={css`
                display: flex;
                justify-content: flex-start;
                align-items: center;
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.NEUTRAL_0}
              >
                {displayText}
              </Text>
            </div>
          </Tooltip>
        )
      },
    },
  ]

  // Effect to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [onlyDisplayActive, scheduleId])

  return (
    <div
      className='player-suspensions-list'
      css={css`
        display: flex;
        flex-direction: column;
        min-height: 98px;
        margin-top: 24px;
        padding: 20px 16px;
        background-color: ${colors.SECONDARY_300};
        border: 1px solid #3f4753;
        border-radius: 8px;
        ${isMobile &&
        css`
          padding: 16px 8px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          padding-bottom: 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_200};

          ${isMobile &&
          css`
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          `}
        >
          <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
            {onlyDisplayActive ? 'ACTIVE SUSPENSIONS' : 'SUSPENSIONS'}
          </Text>
          {/* <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.ATTENDANCE_GRAY}
          >
            {isProfileView
              ? 'Your Suspension History'
              : 'Player Suspension History'}
          </Text> */}
        </div>
      </div>

      {/* Table component */}
      <div
        css={css`
          overflow-x: auto;
          width: 100%;
        `}
      >
        <Table
          dataSource={filteredSuspensions.map((suspension, index) => ({
            ...suspension,
            key: suspension.id || index,
          }))}
          // @ts-ignore
          columns={columns}
          loading={loading}
          pagination={{
            pageSize: 20,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          bordered={true}
          size='small'
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.ATTENDANCE_GRAY}
              >
                {error
                  ? 'Error loading suspensions'
                  : isProfileView
                  ? 'You have no suspensions'
                  : 'No suspensions found'}
              </Text>
            ),
          }}
          css={css`
            .ant-table {
              border-radius: 8px;
              overflow: hidden;
            }
            .ant-table-container {
              border-radius: 8px;
              overflow: hidden;
            }
            .ant-table-thead > tr > th {
              background-color: ${colors.SECONDARY_500};
              color: ${colors.NEUTRAL_0};
              text-align: center;
            }
            .ant-table-thead > tr > th:first-of-type {
              border-top-left-radius: 8px;
              text-align: left;
              position: sticky;
              left: 0;
              z-index: 1;
            }
            .ant-table-thead > tr > th:last-child {
              border-top-right-radius: 8px;
            }
            .ant-table-tbody > tr > td {
              vertical-align: middle;
            }
            .ant-table-tbody > tr:nth-of-type(odd) > td {
              background-color: ${colors.SECONDARY_500};
            }
            .ant-table-tbody > tr:nth-of-type(even) > td {
              background-color: ${colors.SECONDARY_400};
            }
            .ant-table-tbody > tr > td:first-of-type {
              position: sticky;
              left: 0;
              z-index: 1;
            }
            .ant-table-tbody > tr:nth-of-type(odd) > td:first-of-type {
              background-color: ${colors.SECONDARY_500};
            }
            .ant-table-tbody > tr:nth-of-type(even) > td:first-of-type {
              background-color: ${colors.SECONDARY_400};
            }
            .ant-table-tbody > tr:last-child > td:first-of-type {
              border-bottom-left-radius: 8px;
            }
            .ant-table-tbody > tr:last-child > td:last-of-type {
              border-bottom-right-radius: 8px;
            }
            .ant-table-tbody > tr:hover > td {
              background-color: ${colors.SECONDARY_200} !important;
            }
            .ant-pagination {
              margin-top: 16px;
              text-align: center;
            }
            .ant-pagination-item,
            .ant-pagination-prev,
            .ant-pagination-next {
              background-color: ${colors.SECONDARY_400};
              border-color: ${colors.SECONDARY_200};
            }
            .ant-pagination-item-active {
              background-color: ${colors.PRIMARY};
              border-color: ${colors.PRIMARY};
            }
            .ant-pagination-item a {
              color: ${colors.NEUTRAL_0};
            }
            .ant-pagination-item-active a {
              color: ${colors.NEUTRAL_100};
            }
            .ant-pagination-prev button,
            .ant-pagination-next button {
              color: ${colors.NEUTRAL_0};
            }
          `}
        />
      </div>
    </div>
  )
}

export default PlayerSuspensionsList
